import type { Signal } from '@preact/signals-react';
import { cloneDeep } from 'lodash';
import { type ContinuousScrollerFilter, type DataGridColumnSettings } from '../../../index';
import {
  calculateRowWidth,
  CELL_RIGHT_MARGIN_PX,
  getColumnDefaultWidth,
  getColumnMaxWidth,
  getColumnMinWidth,
} from '../data-grid-functions';

export function reorderSignalColumns(
  columns: Signal<Array<DataGridColumnSettings>>,
  columnOrder: Array<string | number>
) {
  const changedColumns: Array<DataGridColumnSettings> = [];
  addInvisibleSignalFields(columns, columnOrder);

  for (let i = 0, length = columnOrder.length; i < length; i++) {
    const foundColumn = columns.value.find((column) => column.id === columnOrder[i]);
    if (foundColumn) {
      changedColumns.push(foundColumn);
    }
  }

  columns.value = [...changedColumns];
}

function addInvisibleSignalFields(columns: Signal<Array<DataGridColumnSettings>>, columnIds: Array<string | number>) {
  const currentColumnsLength = columns.value.length;
  // only run the for loop if there are invisible columns
  if (columnIds.length !== currentColumnsLength) {
    // this loop goes through the columns in their previous order and adds the invisible ones back in
    for (let i = 0; i < currentColumnsLength; i++) {
      if (!columnIds.includes(columns.value[i].id)) {
        columnIds.splice(i, 0, columns.value[i].id);
      }
    }
  }
}

export function calculateSignalRowWidth(columns: Signal<Array<DataGridColumnSettings>>) {
  let rowWidth = 0;
  for (let i = 0, length = columns.value.length; i < length; i++) {
    const column = columns.value[i];
    if (column.visible) {
      rowWidth += (column.width || getColumnDefaultWidth(column)) + CELL_RIGHT_MARGIN_PX;
    }
  }

  return rowWidth;
}

export function changeSignalColumnVisibility(
  columns: Signal<Array<DataGridColumnSettings>>,
  rowWidth: Signal<number>,
  indicatorRowWidth: Signal<number>,
  changedColumns: Array<DataGridColumnSettings>,
  availableWidth?: number
) {
  // update visibilities
  for (let i = 0, length = changedColumns.length; i < length; i++) {
    const foundColumn = columns.value.find((column) => changedColumns[i].id === column.id);
    if (foundColumn) {
      foundColumn.visible = changedColumns[i].visible;
    }
  }

  rowWidth.value = calculateSignalRowWidth(columns);

  // adjust columns to take available width
  if (availableWidth && availableWidth > rowWidth.value) {
    const finalAvailableWidth = availableWidth - indicatorRowWidth.value;
    adjustSignalColumnsToTakeAvailableSpace(finalAvailableWidth, columns);
    rowWidth.value = calculateSignalRowWidth(columns);
  } else {
    columns.value = cloneDeep(columns.value);
  }
}

export function changeSignalColumnSize(
  columns: Signal<Array<DataGridColumnSettings>>,
  rowWidth: Signal<number>,
  indicatorRowWidth: Signal<number>,
  availableWidth: Signal<number>,
  changedColumns: Array<DataGridColumnSettings>,
  currentAvailableWidth?: number
) {
  // update state
  for (let i = 0, length = changedColumns.length; i < length; i++) {
    const foundColumn = columns.value.find((column) => changedColumns[i].id === column.id);
    if (foundColumn) {
      foundColumn.width = changedColumns[i].width;
    }
  }

  rowWidth.value = calculateSignalRowWidth(columns);

  // adjust columns to take available width if availableWidth has grown OR shrunk
  if (currentAvailableWidth) {
    const finalAvailableWidth = currentAvailableWidth - indicatorRowWidth.value;

    if (currentAvailableWidth && currentAvailableWidth > rowWidth.value - indicatorRowWidth.value) {
      adjustSignalColumnsToTakeAvailableSpace(finalAvailableWidth, columns);
      rowWidth.value = calculateSignalRowWidth(columns);
    } else {
      columns.value = cloneDeep(columns.value);
    }

    availableWidth.value = finalAvailableWidth;
  }
}

// Updates the visible columns widths to fill up the available space
function adjustSignalColumnsToTakeAvailableSpace(totalWidth: number, columns: Signal<Array<DataGridColumnSettings>>) {
  // calculate the remaining width
  const rowWidth = calculateSignalRowWidth(columns);
  const remainingWidth = totalWidth - rowWidth;
  //Check if available space has shrunk or grown
  if (Math.abs(remainingWidth) < 1) {
    return;
  }

  const clonedColumns = cloneDeep(columns.value);
  let lastVisibleColumn: DataGridColumnSettings | undefined;

  //adjust each column to fill up the available space
  for (let i = 0, length = clonedColumns.length; i < length; i++) {
    const column = clonedColumns[i];
    if (column.visible && column.width) {
      // calculate the column scaling factor rounded to 2 decimal places
      const factor = Math.round((column.width / rowWidth) * 100) / 100;
      const minWidth = getColumnMinWidth(column);
      const maxWidth = getColumnMaxWidth(column);

      // increase the columns width by the factor of the remaining width
      //ensure column width is within min and max values
      column.width = Math.max(minWidth, Math.min(column.width + Math.floor(remainingWidth * factor), maxWidth));

      // store for residual width update
      lastVisibleColumn = column;
    }
  }

  // check for residual width due to rounding errors
  const adjustedRowWidth = calculateRowWidth(clonedColumns);
  if (totalWidth > adjustedRowWidth && lastVisibleColumn) {
    // assign residual space to last column
    const residualWidth = totalWidth - adjustedRowWidth;
    lastVisibleColumn.width! += residualWidth;
  }

  columns.value = clonedColumns;
}

export function updateColumnFilters(
  changedColumns: Array<DataGridColumnSettings>,
  filters: Signal<Array<ContinuousScrollerFilter>>
) {
  let newFilters: Array<ContinuousScrollerFilter> = filters.value;

  for (let i = 0, length = changedColumns.length; i < length; i++) {
    const column = changedColumns[i];
    if (!column.visible) {
      newFilters = newFilters.filter((filter) => filter.field !== column.field);
    }
  }

  if (newFilters.length !== filters.value.length) {
    filters.value = newFilters;
  }
}
